import { useEffect } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";

import { zodResolver } from "@hookform/resolvers/zod";

import { FormGroup } from "@cofano/cck";

import { FormDialog } from "components/dialogs/Dialogs";

import { Language } from "imports/Utils";

import InvoiceGroupTypeSelect from "view/invoicegrouptypes/components/selects/InvoiceGroupTypeSelect";
import { invoiceGroupTypeSettingsSchema, InvoiceGroupTypeSettingsSchemaInput, InvoiceGroupTypeSettingsSchemaOutput } from "view/invoicegrouptypes/utils/invoiceGroupType.schema";

import {
	GetInvoiceGroupTypesDocument,
	GetInvoiceGroupTypeSettingsDocument,
	GetInvoiceGroupTypeSettingsQuery,
} from "__generated__/view/invoicegrouptypes/graphql/invoiceGroupType.queries.generated";
import {
	SaveInvoiceGroupTypeSettingsDocument,
	SaveInvoiceGroupTypeSettingsMutation,
} from "__generated__/view/invoicegrouptypes/graphql/invoiceGroupType.mutations.generated";

const InvoiceGroupTypeSettingsDialog = ({ open, onClose }: {
	open: boolean;
	onClose: () => void;
}) => {

	const { data: typesData } = useQuery(GetInvoiceGroupTypesDocument);
	const invoiceGroupTypes = typesData?.invoiceGroupTypes ?? [];

	const [ saveSettings, { loading: submitting } ] = useMutation<SaveInvoiceGroupTypeSettingsMutation>(SaveInvoiceGroupTypeSettingsDocument);
	const [ getSettings, { loading } ] = useLazyQuery<GetInvoiceGroupTypeSettingsQuery>(GetInvoiceGroupTypeSettingsDocument);

	const defaultValues = {
		advancePayment: null,
		advancePaymentCorrection: null,
	};

	const { control, handleSubmit, reset } = useForm<InvoiceGroupTypeSettingsSchemaInput, unknown, InvoiceGroupTypeSettingsSchemaOutput>({
		resolver: zodResolver(invoiceGroupTypeSettingsSchema),
		defaultValues: defaultValues,
	});

	const handleOnClose = () => {
		onClose();
		reset(defaultValues);
	};

	const TypeSelect = ({ value, onChange, excludeId }: { value: number | null, onChange: (id: number | null) => void, excludeId: number | null }) => {
		return (
			<InvoiceGroupTypeSelect
				includeAdvancePayment
				charterType={null}
				value={value != null ? invoiceGroupTypes.find(type => type.id === value) : undefined}
				getOptionDisabled={option => excludeId != null && option.id === excludeId}
				onChange={option => onChange(option?.id ?? null)} />
		);
	};

	const save = (data: InvoiceGroupTypeSettingsSchemaOutput) => {
		saveSettings({
			variables: {
				input: data,
			},
		}).then(() => {
			handleOnClose();
		});
	};

	useEffect(() => {
		if (open) {
			getSettings().then(({ data }) => {
				reset({
					advancePayment: data?.invoiceGroupTypeSettings?.advancePayment?.id ?? null,
					advancePaymentCorrection: data?.invoiceGroupTypeSettings?.advancePaymentCorrection?.id ?? null,
				});
			});
		}
	}, [ open ]);

	const [ advancePayment, advancePaymentCorrection ] = useWatch({ control, name: [ "advancePayment", "advancePaymentCorrection" ] });

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "INVOICE_GROUP_TYPE_SETTINGS" })}
			form='invoiceGroupTypeSettingsForm'
			loading={loading || submitting}>
			<form onSubmit={(event) => void handleSubmit(save)(event)} id="invoiceGroupTypeSettingsForm">
				<FormGroup label={Language("INVOICE_GROUP_TYPE_SETTINGS_ADVANCE_PAYMENT")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="advancePayment"
						control={control}
						render={({ field }) => <TypeSelect {...field} excludeId={advancePaymentCorrection ?? null} />} />
				</FormGroup>

				<FormGroup label={Language("INVOICE_GROUP_TYPE_SETTINGS_ADVANCE_PAYMENT_CORRECTION")} labelPlacement="left-center" overflow="visible">
					<Controller
						name="advancePaymentCorrection"
						control={control}
						render={({ field }) => <TypeSelect {...field} excludeId={advancePayment ?? null} />} />
				</FormGroup>
			</form>
		</FormDialog>
	);
};

export default InvoiceGroupTypeSettingsDialog;
